/**
 * Componentes de apresentação da "Atualização do Instrumento" (redesenho do
 * protótipo). Puramente visuais: recebem dados/handlers e renderizam classes do
 * `workbench.css`. A lógica vive em `dominio.ts`.
 */
import { type ReactNode, useState } from "react";
import type { ItemMigracaoRemoto } from "../../../lib/api.ts";
import {
  type AcaoAvaliador,
  type Conferencia,
  FASES,
  acaoDaDecisao,
  corSetor,
  faseDoEstado,
  nomeSetor,
  rotuloDecisao,
  rotuloTipo,
} from "./dominio.ts";

const LABEL_CAMPO: Record<string, string> = {
  processo: "processo",
  norma: "norma/critério",
  evidencia: "evidência",
};

// ── Ícones (SVG stroke, currentColor) — equivale ao `Ico` do protótipo ───────

export function Ico({ name, size = 18 }: { name: string; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {CAMINHO[name] ?? null}
    </svg>
  );
}

const CAMINHO: Record<string, ReactNode> = {
  check: <path d="M20 6 9 17l-5-5" />,
  back: <path d="M15 18l-6-6 6-6" />,
  chevron: <path d="M6 9l6 6 6-6" />,
  x: <path d="M18 6 6 18M6 6l12 12" />,
  undo: (
    <>
      <path d="M9 14 4 9l5-5" />
      <path d="M4 9h11a5 5 0 0 1 0 10h-2" />
    </>
  ),
  swap: (
    <>
      <path d="M7 8h12l-3-3" />
      <path d="M17 16H5l3 3" />
    </>
  ),
  shield: <path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z" />,
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5" />
      <path d="M12 8h.01" />
    </>
  ),
  alert: (
    <>
      <path d="M12 3l9 16H3z" />
      <path d="M12 10v4" />
      <path d="M12 17h.01" />
    </>
  ),
  bell: (
    <>
      <path d="M6 9a6 6 0 0 1 12 0c0 5 2 6 2 6H4s2-1 2-6" />
      <path d="M10 20a2 2 0 0 0 4 0" />
    </>
  ),
  people: (
    <>
      <circle cx="9" cy="8" r="3" />
      <path d="M3 20a6 6 0 0 1 12 0" />
      <path d="M16 6a3 3 0 0 1 0 6" />
      <path d="M21 20a6 6 0 0 0-4-5.6" />
    </>
  ),
  crown: <path d="M3 7l4 4 5-7 5 7 4-4-2 12H5z" />,
  text: (
    <>
      <path d="M5 7h14" />
      <path d="M5 12h10" />
      <path d="M5 17h7" />
    </>
  ),
  edit: (
    <>
      <path d="M4 20h4l10-10-4-4L4 16z" />
      <path d="M13.5 6.5l4 4" />
    </>
  ),
  play: <path d="M7 5l11 7-11 7z" />,
  eye: (
    <>
      <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7-10-7-10-7z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
};

// ── Stepper de 4 fases ───────────────────────────────────────────────────────

export function Stepper({ estado, compact = false }: { estado: string; compact?: boolean }) {
  const ativa = faseDoEstado(estado);
  return (
    <div className={`wb-stepper${compact ? " compact" : ""}`}>
      {FASES.map((f, i) => {
        const cls = i < ativa ? "done" : i === ativa ? "active" : "todo";
        return (
          <div key={f.titulo} className={`fluxo-step ${cls}`}>
            <div className={`fluxo-node ${cls}`}>
              {cls === "done" ? <Ico name="check" size={16} /> : i + 1}
            </div>
            <div className="fluxo-tit">{f.titulo}</div>
            <div className="fluxo-desc">{f.desc}</div>
          </div>
        );
      })}
    </div>
  );
}

// ── Tags e chips ─────────────────────────────────────────────────────────────

export function SetorTag({ setor }: { setor: string }) {
  return (
    <span className="setor-tag">
      <span className="setor-ponto" style={{ background: corSetor(setor) }} />
      {nomeSetor(setor)}
    </span>
  );
}

export function TipoMudanca({ tipo }: { tipo: string }) {
  const r = rotuloTipo(tipo);
  return (
    <span className={`tipo-tag tom-${r.tom}`}>
      <Ico name={r.icone} size={13} />
      {r.texto}
    </span>
  );
}

export function CodigoTecnico({ item }: { item: ItemMigracaoRemoto }) {
  const temDestino = item.codigoDestino && item.codigoDestino !== item.codigoOrigem;
  return (
    <span className="cod-tec">
      {item.codigoOrigem}
      {temDestino ? ` → ${item.codigoDestino}` : ""}
    </span>
  );
}

export function DecisaoEcho({ decisao }: { decisao: string }) {
  const a = acaoDaDecisao(decisao);
  if (!a) {
    return null;
  }
  return (
    <span className={`dec-echo ${a}`}>
      <Ico name="check" size={13} />
      {rotuloDecisao(decisao)}
    </span>
  );
}

// ── Diff da pergunta (protagonista) ──────────────────────────────────────────

export function DiffPergunta({ item, hero = false }: { item: ItemMigracaoRemoto; hero?: boolean }) {
  const mudou = !!item.perguntaNova && item.perguntaNova !== item.perguntaAtual;
  const renumerado = item.codigoDestino && item.codigoDestino !== item.codigoOrigem;
  const outros = Object.keys(item.camposTexto ?? {})
    .filter((k) => k !== "pergunta")
    .map((k) => LABEL_CAMPO[k] ?? k);
  return (
    <div className={`diff${hero ? " hero" : ""}`}>
      <div className="diff-row diff-old">
        <span className="diff-lbl">Hoje</span>
        <span className="diff-txt">{item.perguntaAtual ?? "—"}</span>
      </div>
      {mudou && (
        <div className="diff-row diff-new">
          <span className="diff-lbl">Sugerido</span>
          <span className="diff-txt">{item.perguntaNova}</span>
        </div>
      )}
      {renumerado && (
        <div className="diff-extra">
          Item renumerado — as avaliações já feitas são transferidas, sem perda.
        </div>
      )}
      {outros.length > 0 && (
        <div className="diff-extra" style={{ color: "var(--text-muted)" }}>
          Também atualiza: {outros.join(", ")}.
        </div>
      )}
    </div>
  );
}

// ── Impacto inline ───────────────────────────────────────────────────────────

export function ImpactoInline({ item, onAbrir }: { item: ItemMigracaoRemoto; onAbrir: () => void }) {
  if (!item.temAvaliacao) {
    return <span className="impacto-inline vazio">Nenhuma avaliação registrada ainda</span>;
  }
  return (
    <span className="impacto-inline">
      <button type="button" className="impacto-link" onClick={onAbrir}>
        {item.nRespostas ?? 0} avaliações e {item.nEvidencias ?? 0} evidências já registradas · ver
      </button>
    </span>
  );
}

// ── 3 ações leigas ───────────────────────────────────────────────────────────

const ACOES: { acao: AcaoAvaliador; rotulo: string; cls: string }[] = [
  { acao: "aceitar", rotulo: "Aceitar a mudança sugerida", cls: "acao-aceitar" },
  { acao: "manter", rotulo: "Manter como está", cls: "acao-manter" },
  { acao: "descartar", rotulo: "Descartar", cls: "acao-descartar" },
];

export function DecisaoLeiga({
  decisao,
  onDecidir,
  disabled = false,
}: {
  decisao: string;
  onDecidir: (a: AcaoAvaliador) => void;
  disabled?: boolean;
}) {
  const ativa = acaoDaDecisao(decisao);
  return (
    <div className="acoes">
      {ACOES.map((a) => {
        const on = ativa === a.acao;
        return (
          <button
            key={a.acao}
            type="button"
            className={`acao-btn ${a.cls}${on ? " is-on" : ""}`}
            disabled={disabled}
            onClick={() => onDecidir(a.acao)}
          >
            {on && <Ico name="check" size={14} />}
            {a.rotulo}
          </button>
        );
      })}
    </div>
  );
}

// ── Banner ───────────────────────────────────────────────────────────────────

export function Banner({
  tone,
  icone,
  titulo,
  children,
}: {
  tone: "info" | "success" | "warn" | "danger";
  icone: string;
  titulo: string;
  children?: ReactNode;
}) {
  return (
    <div className={`wb-banner ${tone}`}>
      <span className="wb-banner-ico">
        <Ico name={icone} size={18} />
      </span>
      <div>
        <b>{titulo}</b>
        {children && <p>{children}</p>}
      </div>
    </div>
  );
}

// ── Grupo de decisão (presidente, "o que muda") ──────────────────────────────

export function GrupoDecisao({
  titulo,
  desc,
  tom,
  itens,
  onImpacto,
}: {
  titulo: string;
  desc: string;
  tom: "texto" | "renumerado" | "manter" | "descartar";
  itens: ItemMigracaoRemoto[];
  onImpacto: (i: ItemMigracaoRemoto) => void;
}) {
  const [aberto, setAberto] = useState(false);
  const vazio = itens.length === 0;
  return (
    <div className={`grupo${aberto ? " aberto" : ""}${vazio ? " grupo-vazio" : ""}`}>
      <button type="button" className="grupo-head" onClick={() => !vazio && setAberto((o) => !o)}>
        <span className={`grupo-n tom-${tom}`}>{itens.length}</span>
        <span className="grupo-meta">
          <span className="grupo-tit">{titulo}</span>
          <span className="grupo-desc">{desc}</span>
        </span>
        {!vazio && (
          <span className="grupo-chev">
            <Ico name="chevron" size={18} />
          </span>
        )}
      </button>
      {aberto && (
        <div className="grupo-itens">
          {itens.map((it) => (
            <div key={it.id} className="gi">
              <SetorTag setor={it.setor} />
              <div className="gi-perg">{it.perguntaNova ?? it.perguntaAtual ?? "—"}</div>
              {it.temAvaliacao && (
                <button type="button" className="gi-link" onClick={() => onImpacto(it)}>
                  {it.nRespostas ?? 0} avaliações preservadas · ver
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Conferência de segurança ─────────────────────────────────────────────────

const CONF_ICO: Record<string, string> = { ok: "check", bloqueio: "x", atencao: "alert" };

export function ConfCard({ conf }: { conf: Conferencia }) {
  return (
    <div className="conf-card">
      {conf.checks.map((k) => (
        <div key={`${k.estado}-${k.titulo}-${k.detalhe}`} className={`conf-item conf-${k.estado}`}>
          <span className="conf-ico">
            <Ico name={CONF_ICO[k.estado] ?? "info"} size={15} />
          </span>
          <div>
            <div className="conf-titulo">{k.titulo}</div>
            <div className="conf-detalhe">{k.detalhe}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Barra de progresso segmentada ────────────────────────────────────────────

export function ProgTrack({ a, m, d }: { a: number; m: number; d: number }) {
  if (a + m + d === 0) {
    return <div className="prog-track" />;
  }
  return (
    <div className="prog-track">
      {a > 0 && <div className="prog-seg aceitar" style={{ flex: a }} />}
      {m > 0 && <div className="prog-seg manter" style={{ flex: m }} />}
      {d > 0 && <div className="prog-seg descartar" style={{ flex: d }} />}
    </div>
  );
}
